import * as React from "react"
import {
  CmsVendor,
  SourceControlProvider,
  SourceOrganization,
} from "@modules/graphql/types"
import { useSourceProviderTeamsForUserQuery } from "@modules/site/create/Import/queries.generated"
import {
  getCurrentSourceProviderTeam,
  getDefaultSiteName,
  RepoFormActionType,
  useRepoForm,
} from "@modules/site/create/Import/components/PickRepository.helpers"
import { InstallationField } from "@modules/onboarding/createFromExisting/components/PickRepository.fields"
import { useOrganizationNameFieldsFragment } from "@modules/organization/Settings/fragments.generated"
import useInstallationWindow from "@modules/toolkit/closable-window/useInstallationWindow"
import {
  ErrorAlert,
  useTriggerErrorAlert,
} from "@modules/ui/components/ErrorAlert"
import { useValidateUniqueSiteName } from "@modules/site/shared/hooks/useValidateUniqueSiteName"
import { Button, InputFieldBlock, Spacer, ThemeCss } from "gatsby-interface"
import Loading from "@modules/ui/components/Loading"
import formatRepoName from "@modules/toolkit/helpers/formatRepoName"
import { StyledForm } from "@modules/ui/components/Primitives"
import { MdArrowDownward } from "react-icons/md"
import {
  provisionSite as provisionSiteText,
  ui as uiText,
} from "@modules/locales/default.js"
import { useProvisionSite } from "@modules/site/create/Provision/hooks/useProvisionSite"
import { NarrowSingleColumn } from "@modules/ui/layouts/Containers"
import { NoInstallations } from "@modules/site/create/shared/components/NoInstallations"

type FieldWrapperProps = {
  size?: number
  withLeftSpacing?: boolean
  children?: React.ReactNode
}
function FieldWrapper({ size, withLeftSpacing, children }: FieldWrapperProps) {
  return (
    <div
      css={theme => ({
        flex: size || 1,
        [theme.mediaQueries.tablet]: {
          marginLeft: withLeftSpacing ? theme.space[7] : 0,
        },
      })}
    >
      {children}
    </div>
  )
}

const formSectionCss: ThemeCss = theme => ({
  display: "flex",
  width: "100%",
  flexWrap: "wrap",
  flexDirection: "column",
  marginBottom: theme.space[7],
  [theme.mediaQueries.tablet]: {
    flexWrap: "nowrap",
    flexDirection: "row",
  },
})

const notAuthenticatedWithGitHubError = "has not been signed in with GITHUB"

type ProvisionBasicConfigurationProps = {
  organizationId: string
  starterName: string
  starterVendor: CmsVendor
  onSiteCreated: (siteId: string) => void
  isCurrentStep: boolean
}

export function ProvisionBasicConfiguration({
  organizationId,
  starterName,
  starterVendor,
  onSiteCreated,
  isCurrentStep,
}: ProvisionBasicConfigurationProps) {
  const [formState, dispatch] = useRepoForm()
  const [hasClosedWindow, setHasClosedWindow] = React.useState(false)
  const [repoName, setRepoName] = React.useState<string>(
    starterName ? starterName.split("/")[1] : ""
  )
  const [setError, errorAlert] = useTriggerErrorAlert()
  const [provisionSite, provisionSiteLoading] = useProvisionSite({ setError })
  const [
    verifySiteNameUniqueness,
    { loading: loadingSiteNameUniqueness, error: serverError, validationError },
  ] = useValidateUniqueSiteName()
  // Hook for installing new organization if one not available
  const { popInstallationWindow } = useInstallationWindow(
    (installationId: string) => {
      refetch()

      if (installationId) {
        dispatch({
          type: RepoFormActionType.SelectInstallation,
          payload: installationId,
        })
      }

      setHasClosedWindow(true)
    }
  )
  // Get organizations for given git provider
  const { data, loading, error, refetch } = useSourceProviderTeamsForUserQuery({
    variables: {
      provider: SourceControlProvider.Github, // starters are only available with github currently, so we are safe to hard code
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  })

  const sourceOrganizations = (data && data.sourceProviderTeamsForUser) || []
  const { data: orgNameData } = useOrganizationNameFieldsFragment(
    organizationId
  )

  const selectedInstallation = getCurrentSourceProviderTeam(
    formState.installation,
    orgNameData?.name,
    sourceOrganizations as SourceOrganization[]
  )

  /*
    Loading and Error States
  */

  if (loading && sourceOrganizations.length === 0) {
    return (
      <Loading
        delay={1000}
        message={provisionSiteText.messages.loadingSourceProviderTeams}
        bufferSize="padded"
      />
    )
  }

  // only show the error if it isn't related to GitHub
  // not being authenticated with yet
  if (error && !error?.message.includes(notAuthenticatedWithGitHubError)) {
    return (
      <NarrowSingleColumn>
        <ErrorAlert>{error.message}</ErrorAlert>
      </NarrowSingleColumn>
    )
  }

  if (sourceOrganizations.length === 0 || !selectedInstallation) {
    return (
      <NoInstallations
        hasClosedWindow={hasClosedWindow}
        popInstallationWindow={popInstallationWindow}
        provider={SourceControlProvider.Github}
        organizationId={organizationId}
      />
    )
  }

  // Form submission callback
  const onSubmit: React.FormEventHandler<HTMLFormElement> = async e => {
    e.preventDefault()
    // verify sitename uniqueness
    const isValidSiteName = await verifySiteNameUniqueness({
      name: getDefaultSiteName(repoName, "main"), // default main branch to 'main'
      sourceOrganizationId: selectedInstallation?.id,
      organizationId: organizationId,
      sourceControlProvider: SourceControlProvider.Github,
    })
    // check that we have the necessary information
    if (selectedInstallation?.id && repoName && isValidSiteName) {
      setError(null)
      await provisionSite({
        organizationId,
        provider: SourceControlProvider.Github,
        selectedInstallationId: selectedInstallation.id,
        projectName: formatRepoName(repoName),
        vendor: starterVendor,
        templateId: starterName,
        successAction: onSiteCreated,
      })
    }
  }

  const disableSubmitButton =
    !selectedInstallation?.id || repoName === "" || !isCurrentStep

  return provisionSiteLoading ? (
    <Loading
      message={provisionSiteText.messages.creatingSite}
      bufferSize="padded"
    />
  ) : (
    <StyledForm onSubmit={onSubmit}>
      {errorAlert && (
        <React.Fragment>
          {errorAlert}
          <Spacer size={3} />
        </React.Fragment>
      )}
      <div css={formSectionCss}>
        <FieldWrapper>
          <InstallationField
            label="Github organization"
            sourceOrganizations={sourceOrganizations}
            selectedInstallation={selectedInstallation?.id || ""}
            onSelect={(value: string) => {
              dispatch({
                type: RepoFormActionType.SelectInstallation,
                payload: value,
              })
            }}
            popInstallationWindow={popInstallationWindow}
            provider={SourceControlProvider.Github}
          />
        </FieldWrapper>
        <FieldWrapper withLeftSpacing>
          <InputFieldBlock
            id="repoName"
            name="name"
            label="Repository"
            placeholder="Provide unique repository name"
            required
            value={repoName}
            error={validationError || serverError}
            onChange={e => {
              setRepoName(formatRepoName(e.target.value))
            }}
          />
        </FieldWrapper>
      </div>
      <Button
        disabled={disableSubmitButton}
        loading={loadingSiteNameUniqueness}
        rightIcon={<MdArrowDownward />}
        type="submit"
      >
        {uiText.actions.next}
      </Button>
    </StyledForm>
  )
}
